import { Drawer, Grid, ListItemIcon, MenuItem, Select } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import drillXLogo from 'assets/drillx.png';
import energyEfficiencyLogo from 'assets/energy_efficiency_logo.png';
import { ReactComponent as WellGuardianLogo } from 'assets/wg-well-guardian-logo-full.svg';
import clsx from 'clsx';
import Button from 'components/Button';
import TotalLogo from 'components/TotalLogo';
import { APPS } from 'constants/appModules';
import { PATHS } from 'constants/paths';
import { isAllowedToWellGuardian } from 'helpers/UserHelper';
import { getAppFromPath } from 'helpers/pathParser';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import { useSelectUser } from 'redux/User/selectors';

import styles from './Sidebar.module.scss';
import SidebarContentDrillX from './components/SidebarContentDrillX';
import SidebarContentEnergyEnergy from './components/SidebarContentEnergyEffiency/SidebarContentEnergyEffiency';
import SidebarContentWellGuardian from './components/SidebarContentWellGuardian';

type Props = {
  isOpen: boolean;
  onSidebarChanged: () => void;
};

const Sidebar = ({ isOpen, onSidebarChanged }: Props) => {
  const intl = useIntl();
  const match = useRouteMatch();
  const history = useHistory();
  const user = useSelectUser();
  const currentApp = getAppFromPath(match.path);

  const isWellGuardianDisabled = !isAllowedToWellGuardian(user?.profile);

  const [selectedApp, setSelectedApp] = useState<HTMLElement | string>(currentApp);
  const [isAppChanged, setAppChanged] = useState<boolean>(false);
  const moduleSelectContainer = useRef(null);

  const isOpenDelayed = useDebouncedValue(isOpen, parseInt(styles.transitionDurationInMs));

  // open and close are specific selector names in css to track the sidebar state
  const getStylePerState = (basicStyle: string) =>
    clsx(basicStyle, isOpen ? styles.open : styles.close);

  const handleLinkChange = () => PATHS.WELL_GUARDIAN_WELL_HEALTH_HOME;

  const onChangeApp = (event: any) => {
    setSelectedApp(event.target.value);
    setAppChanged(true);
  };

  // this useEffect is used to set the selected app , when moving back to the previous page
  useEffect(() => {
    if (currentApp === APPS.WELL_GUARDIAN) {
      setSelectedApp(APPS.WELL_GUARDIAN);
    } else if (currentApp === APPS.DRILLX_SMART_OPERATIONS) {
      setSelectedApp(APPS.DRILLX_SMART_OPERATIONS);
    } else if (currentApp === APPS.ENERGY_EFFICIENCY) {
      setSelectedApp(APPS.ENERGY_EFFICIENCY);
    }
  }, [currentApp]);

  // route to the new app, when selected app has changed
  useEffect(() => {
    if (isAppChanged) {
      if (selectedApp === APPS.DRILLX_SMART_OPERATIONS) {
        history.push(generatePath(PATHS.HOME));
      } else if (selectedApp === APPS.WELL_GUARDIAN) {
        history.push(generatePath(handleLinkChange()));
      } else {
        history.push(generatePath(PATHS.ENERGY_EFFICIENCY_HOME));
      }
    }
    setAppChanged(false);
  }, [history, isAppChanged, selectedApp]);

  const getSideBarComponentToRender = () => {
    if (currentApp === APPS.WELL_GUARDIAN) {
      return <SidebarContentWellGuardian />;
    } else if (currentApp === APPS.DRILLX_SMART_OPERATIONS) {
      return <SidebarContentDrillX />;
    } else if (currentApp === APPS.ENERGY_EFFICIENCY) {
      return <SidebarContentEnergyEnergy />;
    }
  };

  return (
    <>
      <Drawer
        variant="permanent"
        className={getStylePerState(styles.drawerContainer)}
        anchor="left"
        open={isOpen}
        PaperProps={{
          className: getStylePerState(styles.sidebarPaper),
        }}
      >
        {(isOpen || isOpenDelayed) && (
          <>
            <Grid innerRef={moduleSelectContainer} container item>
              <Select
                className={styles.moduleSelectContainer}
                value={selectedApp}
                inputProps={{
                  classes: {
                    icon: styles.selectIcon,
                  },
                }}
                MenuProps={{
                  classes: {
                    list: styles.menuItem,
                    paper: styles.paperMenu,
                  },
                }}
                onChange={onChangeApp}
              >
                <MenuItem value={APPS.DRILLX_SMART_OPERATIONS}>
                  <ListItemIcon>
                    <TotalLogo className={styles.totalLogo} />
                    <Grid className={styles.menuLabel}>
                      <img className={styles.drillxLogo} alt="DrillX logo" src={drillXLogo} />
                      <p className={styles.logoText}>
                        {intl.formatMessage({ id: 'sidebar.smart' })}{' '}
                        <span className={styles.logoTextOperations}>
                          {intl.formatMessage({ id: 'sidebar.operations' })}
                        </span>
                      </p>
                    </Grid>
                  </ListItemIcon>
                </MenuItem>
                <MenuItem disabled={isWellGuardianDisabled} value={APPS.WELL_GUARDIAN}>
                  <ListItemIcon>
                    <WellGuardianLogo className={styles.wellGuardianLogo} />
                  </ListItemIcon>
                </MenuItem>
                <MenuItem value={APPS.ENERGY_EFFICIENCY}>
                  <ListItemIcon>
                    <img
                      className={styles.energyEfficiencyLogo}
                      alt="DrillX logo"
                      src={energyEfficiencyLogo}
                    />
                  </ListItemIcon>
                </MenuItem>
              </Select>
            </Grid>
            {getSideBarComponentToRender()}
          </>
        )}
      </Drawer>
      <Button className={getStylePerState(styles.toggleDrawerButton)} onClick={onSidebarChanged}>
        <ChevronLeftIcon classes={{ root: getStylePerState(styles.buttonArrow) }} />
      </Button>
    </>
  );
};

export default Sidebar;
